import axios, { AxiosError } from 'axios';
import type {
  BrandConfig,
  Category,
  Section,
  Article,
  ArticleListItem,
  SearchResult,
  RelatedArticle,
  FeedbackPayload,
  StartDeflectionPayload,
  StartDeflectionResponse,
  UpdateDeflectionPayload,
  SubmitRequestPayload,
  SubmitRequestResponse,
  PortalViewer,
  PortalTicket,
  PortalTicketDetailResponse,
  PortalMessage,
  PortalReplyPayload,
  PortalUpdateRequestPayload,
  RequestScope,
} from '@/types';

// Brand identifier type for URL resolution
interface BrandIdentifier {
  type: 'subdomain' | 'custom_domain';
  value: string;
}

// Get brand identifier from URL
function getBrandIdentifier(): BrandIdentifier | null {
  // Check URL query param first (for local dev preview)
  const urlParams = new URLSearchParams(window.location.search);
  const brandParam = urlParams.get('brand');
  if (brandParam) {
    return { type: 'subdomain', value: brandParam };
  }
  
  const hostname = window.location.hostname;
  
  // Handle localhost scenarios
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return { type: 'subdomain', value: 'help' };
  }

  // Check for *.help.airclou.com pattern (subdomain)
  const subdomainMatch = hostname.match(/^([^.]+)\.help\.airclou\.com$/);
  if (subdomainMatch) {
    return { type: 'subdomain', value: subdomainMatch[1] };
  }
  
  // Check if it's the root help.airclou.com (no brand)
  if (hostname === 'help.airclou.com') {
    return null;
  }
  
  // Assume it's a custom domain
  return { type: 'custom_domain', value: hostname };
}

const api = axios.create({
  baseURL: '/api/portal',
  withCredentials: true,
});

// Add brand subdomain to requests (only for subdomain-based brands)
api.interceptors.request.use((config) => {
  const identifier = getBrandIdentifier();
  if (identifier && identifier.type === 'subdomain') {
    config.headers['X-Brand-Subdomain'] = identifier.value;
  }
  return config;
});

// Guard against success: false with 200 status, then unwrap envelope
api.interceptors.response.use((response) => {
  if (response.data?.success === false) {
    const err = new Error(response.data.error?.message || 'Request failed');
    (err as any).code = response.data.error?.code;
    return Promise.reject(err);
  }
  if (response.data && typeof response.data === 'object' && 'success' in response.data && 'data' in response.data) {
    // For paginated responses, strip `success` but keep `data` and `meta`
    if ('meta' in response.data) {
      const { success: _success, ...rest } = response.data;
      response.data = rest;
    } else {
      response.data = response.data.data;
    }
  }
  return response;
});

// Error handling helper
export interface ApiErrorResponse {
  code: string;
  message: string;
}

export function getErrorCode(error: unknown): string {
  if (error instanceof AxiosError && error.response?.data?.error) {
    return error.response.data.error.code || 'UNKNOWN_ERROR';
  }
  return 'UNKNOWN_ERROR';
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof AxiosError && error.response?.data?.error) {
    return error.response.data.error.message || 'An unexpected error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

// Brand
export async function getBrandConfig(): Promise<BrandConfig> {
  const { data } = await api.get<BrandConfig>('/config');
  return data;
}

// Categories
export async function getCategories(): Promise<Category[]> {
  const { data } = await api.get<Category[]>('/categories');
  return data;
}

export async function getCategoryBySlug(slug: string): Promise<{
  category: Category;
  sections: Section[];
  redirect?: { newSlug: string };
}> {
  try {
    const { data } = await api.get<{ category: Category; sections: Section[] }>(
      `/categories/${slug}`
    );
    return data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 301 && error.response.data?.error?.details?.redirect) {
      return { category: {} as Category, sections: [], redirect: { newSlug: error.response.data.error.details.newSlug } };
    }
    throw error;
  }
}

// Sections
/**
 * Section slugs are unique per category, not per brand, so pass the category
 * slug from the URL whenever we have it — otherwise the backend has to guess
 * between same-named sections in different categories.
 */
export async function getSectionBySlug(slug: string, categorySlug?: string): Promise<{
  section: Section;
  articles: ArticleListItem[];
  redirect?: { newSlug: string; categorySlug?: string };
}> {
  try {
    const { data } = await api.get<{ section: Section; articles: ArticleListItem[] }>(
      `/sections/${slug}`,
      { params: categorySlug ? { category: categorySlug } : undefined }
    );
    return data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 301 && error.response.data?.error?.details?.redirect) {
      const details = error.response.data.error.details;
      // `categorySlug` is the category's *canonical* slug — present when the
      // request named a category, so a rename lands on the canonical URL
      // rather than parking on another stale one.
      return {
        section: {} as Section,
        articles: [],
        redirect: { newSlug: details.newSlug, categorySlug: details.categorySlug },
      };
    }
    throw error;
  }
}

// Articles
export async function getArticleBySlug(slug: string, locale?: string): Promise<{
  article: Article;
  related: RelatedArticle[];
  locale_info?: {
    current: string;
    default: string;
    supported: string[];
    available: string[];
  };
}> {
  const { data } = await api.get<{ article: Article; related: RelatedArticle[]; locale?: { current: string; default: string; supported: string[]; available: string[] } }>(
    `/articles/${slug}`,
    { params: locale ? { locale } : undefined }
  );
  // Backend returns locale info under "locale" key; map to "locale_info" for the frontend
  const { locale: localeData, ...rest } = data;
  return { ...rest, locale_info: localeData };
}

export async function getPopularArticles(limit = 5): Promise<ArticleListItem[]> {
  const { data } = await api.get<ArticleListItem[]>('/articles/popular', {
    params: { limit },
  });
  return data;
}

// Search
export async function searchArticles(
  query: string,
  mode: 'keyword' | 'semantic' | 'hybrid' = 'hybrid'
): Promise<SearchResult[]> {
  const { data } = await api.get<{ results: SearchResult[]; count: number; search_mode_used: string }>('/search', {
    params: { q: query, mode },
  });
  return data.results;
}

// Feedback
export interface FeedbackStatus {
  voted: boolean;
  helpful?: boolean;
}

export async function getFeedbackStatus(articleId: string): Promise<FeedbackStatus> {
  const { data } = await api.get<FeedbackStatus>(`/articles/${articleId}/feedback/status`);
  return data;
}

export async function submitFeedback(payload: FeedbackPayload): Promise<{ id: string; helpful: boolean }> {
  const { article_id, ...body } = payload;
  const { data } = await api.post<{ id: string; helpful: boolean }>(`/articles/${article_id}/feedback`, body);
  return data;
}

// View tracking
export async function trackArticleView(articleId: string): Promise<void> {
  await api.post(`/articles/${articleId}/view`);
}

// Deflection tracking
export async function startDeflection(payload: StartDeflectionPayload): Promise<StartDeflectionResponse> {
  const { data } = await api.post<StartDeflectionResponse>('/deflection', payload);
  return data;
}

export async function updateDeflection(payload: UpdateDeflectionPayload): Promise<void> {
  await api.patch('/deflection', payload);
}

// Submit support request
export async function submitSupportRequest(payload: SubmitRequestPayload): Promise<SubmitRequestResponse> {
  const { data } = await api.post<SubmitRequestResponse>('/requests', payload);
  return data;
}

// Suggested articles for contact form
export async function getSuggestedArticles(query: string): Promise<SearchResult[]> {
  const { data } = await api.get<SearchResult[]>('/suggest', {
    params: { q: query },
  });
  return data;
}

// =====================================================
// My Requests (Ticket Portal)
// =====================================================

export async function getMyRequests(params: {
  status?: string;
  sort?: string;
  order?: string;
  page?: number;
  limit?: number;
  scope?: RequestScope;
}): Promise<{ data: PortalTicket[]; meta: { page: number; limit: number; total: number; totalPages: number } }> {
  const { data } = await api.get<{ data: PortalTicket[]; meta: { page: number; limit: number; total: number; totalPages: number } }>('/requests/list', {
    params,
    timeout: 10000,
  });
  return data;
}

export async function getRequestDetail(ticketId: string): Promise<PortalTicketDetailResponse> {
  const { data } = await api.get<PortalTicketDetailResponse>(`/requests/${ticketId}`, {
    timeout: 10000,
  });
  return data;
}

export async function getRequestMessages(ticketId: string): Promise<PortalMessage[]> {
  const { data } = await api.get<PortalMessage[]>(`/requests/${ticketId}/messages`, {
    timeout: 10000,
  });
  return data;
}

export async function replyToRequest(ticketId: string, payload: PortalReplyPayload): Promise<{ id: string }> {
  const { data } = await api.post<{ id: string }>(`/requests/${ticketId}/messages`, payload, {
    timeout: 15000,
  });
  return data;
}

export async function updateRequest(ticketId: string, payload: PortalUpdateRequestPayload): Promise<void> {
  await api.patch(`/requests/${ticketId}`, payload, {
    timeout: 10000,
  });
}

// Auth - Magic link
export async function requestMagicLink(email: string, returnTo?: string): Promise<void> {
  await api.post('/auth/magic-link', { email, return_to: returnTo });
}

export async function verifyMagicLink(token: string): Promise<PortalViewer> {
  const { data } = await api.get<PortalViewer>('/auth/verify', { params: { token } });
  return data;
}

export async function getSession(): Promise<PortalViewer | null> {
  try {
    const { data } = await api.get<{ authenticated: boolean; email?: string; session_id?: string }>('/auth/me');
    if (!data?.authenticated || !data.email) {
      return null;
    }
    return { email: data.email, session_id: data.session_id || '' };
  } catch {
    return null;
  }
}

export async function signOut(): Promise<void> {
  await api.post('/auth/signout');
}
